/**
 * Sweet Wink Home — brand #1 of 3 piped into the shared brand-home framework
 * (`src/components/shared/brand-home-framework/`). Sweet Wink is a real Faire
 * brand (token `b_gro99lye`): a kids'/baby special-occasion apparel &
 * accessories brand (tutus, graphic tees, hair bows, headbands, dress-up
 * kits, seasonal "mommy & me" pieces) run by a mother/daughter duo in NYC.
 *
 * Every identity fact below is read from what entity-db actually returned for
 * `b_gro99lye` (see `src/data/entity_db/brand/b_gro99lye.ts`) or from Sweet
 * Wink's own live Faire brand page (faire.com/brand/b_gro99lye) — nothing here
 * is invented. `import-collections` returned Sweet Wink's 19 real published
 * collections but 0 items per collection (the documented swallowed-401 gotcha
 * in the framework's SKILL.md); the collection names/hero images/counts below
 * are real (captured before the items call 401'd), but per-collection product
 * tokens are not, so `collections` here is curated straight from those real
 * collection records rather than backed by `productTokens`. The 27 imported
 * products were pulled directly from Sweet Wink's live public Faire brand
 * page (each product URL's `p_...` token), spread across the 8 real category
 * filters that brand page itself exposes (Kids/Baby apparel & accessories,
 * Gear & essentials, Women's apparel & accessories, Storage & organization).
 *
 * This is ONE brand's instance data — the reusable `BrandDataset` type + the
 * rendering framework it feeds live at `src/components/shared/brand-home-framework/`,
 * not here. See that package's SKILL.md for the "pipe in a new brand" procedure.
 */
import { type BrandDataset } from "@faire/prototype-playground/components/shared/brand-home-framework/types";

// Real Faire brand token for Sweet Wink.
export const SWEET_WINK_BRAND_TOKEN = "b_gro99lye";

// Sweet Wink's own real avatar photo (from entity-db; also used as their
// "banner"/hero on the live brand page — this brand hasn't uploaded a
// dedicated wordmark logo asset to Faire).
export const AVATAR_URL =
  "https://cdn.faire.com/fastly/59e1f0961ad6885f4303e6da17454837aa8cd6570ffe2a73c5b4c03946915681.jpeg";
export const BANNER_URL =
  "https://cdn.faire.com/fastly/12b43be0cef7d6803ef175cf346667d7a5b5c0eaf63e1629f60c89e7b8c455e9.png";
// Real script wordmark from Sweet Wink's own site, background-removed (the
// source was an opaque JPEG) so the Masthead's brightness/invert treatment
// renders it as a clean white mark rather than a solid white square.
export const LOGO_SRC = "/images/sweet-wink/logo.png";

// Brand color block. Sweet Wink's own domain (shopsweetwink.com) was
// unreachable from this environment's browser policy, so — rather than invent
// a palette — the color was sampled directly from Sweet Wink's own real
// product photography (the "Raspberry Rhinestone" tutu + matching baby
// headband, a real signature line: bright raspberry pink ~rgb(248,136,184)),
// then darkened (same hue/saturation, lower lightness) so white/cream hero
// text keeps contrast, mirroring how PF Candle Co.'s MAROON was pulled from
// its site. SECONDARY is a light blush tint of the same hue.
export const RASPBERRY = "rgb(157, 21, 89)";
export const RASPBERRY_DEEP = "rgb(108, 14, 61)";
export const RASPBERRY_SECONDARY = "rgb(253, 235, 240)";

// Real collection hero images + item counts, captured by `import-collections`
// before its `/items` call 401'd (see file header). A curated subset of Sweet
// Wink's 19 real published collections.
const SWEET_WINK_COLLECTIONS = [
  {
    label: "Pretty in Pink",
    imageUrl:
      "https://cdn.faire.com/fastly/9174b1ec50170646a6807fc30b456d5c5c53821291851e6d99b67b1ae890e1e8.jpeg",
  },
  {
    label: "Birthday",
    imageUrl:
      "https://cdn.faire.com/fastly/b230428b0b90d212024e40f62d5012bbdbbd79d4f8373cc71df5f6bdd6db50c9.jpeg",
  },
  {
    label: "Back To School",
    imageUrl:
      "https://cdn.faire.com/fastly/d83cd4e4ceffe7d6a2a71455b6ff1fea5c8cd503f6b10e1877b163f9aed1e8fa.jpeg",
  },
  {
    label: "Fall",
    imageUrl:
      "https://cdn.faire.com/fastly/b6ee616ce8196487494b2e108af4a3208a095066d6891b2eaa771281201e1409.jpeg",
  },
];

// Category tiles, derived from the 8 real category filters Sweet Wink's own
// live Faire brand page exposes (Kids/Baby apparel & accessories, Gear &
// essentials, Women's apparel & accessories, Storage & organization) — not
// assumed ahead of time. Keywords are tuned against the 27 real imported
// product names so each tile resolves to that brand's actual matching stock.
const SWEET_WINK_CATEGORY_TILES = [
  {
    label: "Kids apparel",
    keywords: ["skort", "dress up kit", "tutu", "sweater", "denim jacket"],
  },
  {
    label: "Kids accessories",
    keywords: [
      "tote",
      "beanie",
      "hair bow",
      "bow clip",
      "girls dress up headband",
      "birthday headband",
    ],
  },
  { label: "Baby apparel", keywords: ["romper"] },
  { label: "Baby accessories", keywords: ["baby headband"] },
  { label: "Gear & essentials", keywords: ["bib"] },
  {
    label: "Women's apparel",
    keywords: [
      "women's lounge set",
      "women's sweater",
      "women's short sleeve t-shirt",
    ],
  },
  { label: "Women's accessories", keywords: ["gemstone headband"] },
  { label: "Storage & organization", keywords: ["hanger"] },
];

// Real, confirmed "Bestseller"-tagged products on Sweet Wink's own live Faire
// brand page (mock "performance" signal — see the framework's fallback table).
const BESTSELLER_NAME_HINTS: readonly string[] = [
  "raspberry rhinestone",
  "dinosaur sweater",
  "gingham bow patch",
];

// Cross-brand "Better Together" tuning: exclude other kids/baby apparel &
// accessories brands (Sweet Wink's own lane), prioritize adjacent wholesale
// categories a kids boutique retailer would also stock.
const COMPETITOR_KEYWORDS: string[] = [
  "kids clothing",
  "kids apparel",
  "baby clothing",
  "baby apparel",
  "romper",
  "onesie",
  "bodysuit",
  "tutu",
  "dress up",
  "headband",
  "hair bow",
  "legging",
  "skort",
];
const COMPLEMENTARY_KEYWORDS: string[] = [
  "toy",
  "plush",
  "stuffed animal",
  "blanket",
  "swaddle",
  "book",
  "gift",
  "nursery",
  "bath",
  "bottle",
  "sock",
  "shoe",
  "backpack",
  "puzzle",
];

// Sweet Wink's own real description, from the Faire brand profile (entity-db).
const ABOUT_TEXT =
  "Sweet Wink is a lifestyle children's special occasion lifestyle brand designed by a mother/daughter duo in New York City. We aim to make every day a party with our colorful and celebratory collection of graphic tees, tutus, and hair accessories. We design head to toe looks that celebrate life's most special occasions from newborn announcements, to birthday parties, and everyone's favorite holidays. Why do we do it? We believe every day is a party worth celebrating! So throw on your tutu and grab your favorite glitter bow because it's time to get this party started!";

// Sweet Wink — the ONLY dataset for this brand (unlike PF Candle's
// established/mid/new variants, this proof-of-concept sticks to one real,
// fully-populated brand maturity state per the task's scope).
export const SWEET_WINK: BrandDataset = {
  key: "established",
  name: "Sweet Wink",
  logoSrc: LOGO_SRC,
  // The script wordmark's ink is much less dense per pixel of height than a
  // compact all-caps lockup (like PF Candle's) — needs to render taller than
  // the framework's default to read as the same visual weight.
  logoHeight: "64px",
  avatarUrl: AVATAR_URL,
  heroImage: BANNER_URL,
  coverImage: BANNER_URL,
  brandColor: RASPBERRY,
  brandColorDeep: RASPBERRY_DEEP,
  secondaryColor: RASPBERRY_SECONDARY,
  // Collection modules (Kids apparel, Tried & True, the Halloween teaser)
  // read as neutral merchandising rather than brand-themed (per Ryan).
  collectionBackgroundColor: "var(--slate-color-surface-tertiary)",
  // "Kids apparel"'s keyword match would otherwise pick whichever tutu/skort
  // SKU sorts first in the catalog — pin the specific piece Ryan wants there.
  starterKitOverrides: { "Kids apparel": "p_umw3dew5js" },
  location: "New York City, NY",
  minOrder: 300,
  rating: 5,
  reviewCount: 177,
  shipWindow: "Aug 21-Sep 1",
  aboutText: ABOUT_TEXT,
  // Grounded in the brand's real Insider status + real $300 minimum. No real
  // performance-stat copy captured for this brand, so `performanceSignals` is
  // left unset — "Top Shop" alone (below, via hasPerformanceData) is enough.
  promos: ["Free shipping over $300"],
  categoryTiles: SWEET_WINK_CATEGORY_TILES,
  collections: SWEET_WINK_COLLECTIONS,
  hasBestsellers: true,
  hasCollections: true,
  // Real "Top Shop" badge on the brand's live Faire page.
  hasPerformanceData: true,
  // Real Brand Values tags on the brand's live Faire page (per Ryan).
  brandValueTags: ["Not on Amazon", "Women owned"],
  information: [
    { label: "Based in", value: "New York City, NY" },
    { label: "Ships from", value: "United States" },
  ],
  // Verified real handle/domain (web search) — not guessed.
  instagram: "@shopsweetwink",
  website: "shopsweetwink.com",
  // No brand videos imported — the module cleanly renders nothing (see the
  // framework's fallback table); omitted rather than left as a placeholder.
  topCategorySubtitle:
    "Graphic tees, tutus, and dress-up styles for every celebration.",
  bestsellersSubtitle:
    "The pieces retailers keep reordering, season after season.",
  topCategoryHeroImage: SWEET_WINK_COLLECTIONS[0].imageUrl,
  starterKitRangeCopy:
    "apparel, hair accessories, and dress-up styles across our range",
  signaturePicks: ["raspberry rhinestone"],
  bestsellerNameHints: BESTSELLER_NAME_HINTS,
  competitorKeywords: COMPETITOR_KEYWORDS,
  complementaryKeywords: COMPLEMENTARY_KEYWORDS,
  // Real per-star breakdown from the brand's live Faire page (per Ryan).
  reviewDistribution: [
    { stars: 5, count: 175 },
    { stars: 4, count: 1 },
    { stars: 3, count: 1 },
    { stars: 2, count: 0 },
    { stars: 1, count: 0 },
  ],
};
